import React from 'react';
import {useTranslation} from 'react-i18next';
import {isPlainObject} from '../shapes';
import {prettifyKey, humanizeFieldLabel, humanizeValue, isNumeric} from '../humanize';
import {HIDDEN_FIELDS, MONEY_FIELDS} from '../labels';
import {DeltaChip} from '../primitives/DeltaChip';
import {DetailSection} from '../primitives/DetailSection';

// MEDIA_PLAN_CHANGE_APPLIED: un cambio que el sync del Media Plan aplicó en la plataforma.
// Lo valioso para el humano son DOS cosas: qué acción se aplicó y por qué (la evidencia
// citada de la planilla). `before`/`after` son snapshots enteros: se comparan y se muestran
// SOLO los escalares que cambiaron — nunca el objeto crudo.

const MAX_DIFF_ROWS = 8;
// La evidencia es texto libre de una planilla: se corta para que el panel no se vuelva un muro.
const MAX_EVIDENCE_CHARS = 400;
const RAW = {interpolation: {escapeValue: false}};

const ACTION_KEYS = {
    update_budget: 'activity_log.detail.media_plan_action_update_budget',
    pause_campaign: 'activity_log.detail.media_plan_action_pause_campaign',
    activate_campaign: 'activity_log.detail.media_plan_action_activate_campaign',
    update_campaign_budgets: 'activity_log.detail.media_plan_action_update_campaign_budgets',
};

function textOf(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function isPrimitiveValue(value) {
    return value === null || value === undefined || typeof value !== 'object';
}

function hasValue(value) {
    return value !== null && value !== undefined && value !== '';
}

function sideValue(field, value, t) {
    if (!hasValue(value)) return '—';
    const humanized = humanizeValue(field, value, t);
    return humanized === null ? '—' : humanized;
}

// ¿Cambió de verdad? Tolera "800000" vs 800000 (el sync mezcla strings y números).
function sameValue(before, after) {
    if (before === after) return true;
    if (!hasValue(before) && !hasValue(after)) return true;
    if (!hasValue(before) || !hasValue(after)) return false;
    if (isNumeric(before) && isNumeric(after)) return Number(before) === Number(after);
    return String(before) === String(after);
}

function actionText(detail, t) {
    const actionType = textOf(detail.action_type);
    if (!actionType) return t('activity_log.detail.media_plan_action_generic');
    const key = ACTION_KEYS[actionType.toLowerCase()];
    return key ? t(key) : prettifyKey(actionType);
}

function evidenceText(detail) {
    const evidence = textOf(detail.evidence);
    if (!evidence) return null;
    return evidence.length > MAX_EVIDENCE_CHARS ? `${evidence.slice(0, MAX_EVIDENCE_CHARS)}…` : evidence;
}

// Diff de los snapshots: solo keys cuyos DOS lados son primitivos y difieren.
// Lo anidado se ignora en silencio (un snapshot de campaña trae subobjetos enteros).
function scalarRows(detail) {
    const before = isPlainObject(detail.before) ? detail.before : {};
    const after = isPlainObject(detail.after) ? detail.after : {};
    const keys = [];
    const push = key => {
        if (typeof key === 'string' && key && !keys.includes(key) && !HIDDEN_FIELDS.has(key)) keys.push(key);
    };
    Object.keys(before).forEach(push);
    Object.keys(after).forEach(push);

    return keys
        .filter(key => isPrimitiveValue(before[key]) && isPrimitiveValue(after[key]))
        .filter(key => !sameValue(before[key], after[key]))
        .map(key => ({key, before: before[key], after: after[key]}));
}

export function MediaPlanChangeDetail({detail}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const evidence = evidenceText(detail);
    const rows = scalarRows(detail);
    const shown = rows.slice(0, MAX_DIFF_ROWS);
    const rest = rows.length - shown.length;

    return (
        <div className="ald-media-plan-change">
            <div className="ald-story">
                {t('activity_log.detail.media_plan_story', {action: actionText(detail, t), ...RAW})}
            </div>

            {evidence && (
                <DetailSection title={t('activity_log.detail.evidence')}>
                    <div className="ald-evidence">{evidence}</div>
                </DetailSection>
            )}

            {shown.length > 0 && (
                <div className="ald-diff">
                    {shown.map(row => (
                        <div className="ald-diff-row" key={row.key}>
                            <span className="ald-diff-label">{humanizeFieldLabel(row.key, t)}</span>
                            <span className="ald-diff-values">
                                <span className="ald-diff-before">{sideValue(row.key, row.before, t)}</span>
                                <span className="ald-diff-arrow" aria-hidden="true">→</span>
                                <span className="ald-diff-after">{sideValue(row.key, row.after, t)}</span>
                                <DeltaChip before={row.before} after={row.after} money={MONEY_FIELDS.has(row.key)}/>
                            </span>
                        </div>
                    ))}
                    {rest > 0 && (
                        <div className="ald-diff-row">
                            <span className="ald-diff-label">{t('activity_log.detail.more_items', {count: rest})}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Guard del registry: sin acción, evidencia ni snapshots no hay nada que contar.
export function canRenderMediaPlanChange(detail) {
    if (!isPlainObject(detail)) return false;
    return !!textOf(detail.action_type)
        || !!textOf(detail.evidence)
        || isPlainObject(detail.before)
        || isPlainObject(detail.after);
}
